/**
 * Раздел «Шкафы»: наполнение коммуникационных шкафов объекта.
 *
 * Три состояния: объект не выбран, список шкафов объекта, содержимое
 * одного шкафа. Текущее место отражается в адресе страницы.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import { selectGrouped, selectPrefixMap } from '../../store/slices/switchesSlice';
import {
  loadCabinets,
  loadCabinetItems,
  loadUndocumented,
  saveCabinetItems,
  selectCabinets,
  selectCabinetItems,
  selectUndocumented,
} from '../../store/slices/cabinetsSlice';
import { selectCanEditObject } from '../../store/slices/authSlice';
import { getRackHeight, firstFreeUnit, normalizeSwitchName } from '../../domain/racks';
import { objectLabel } from '../../domain/issues';
import ObjectSidebar from '../../components/common/ObjectSidebar';
import RackDiagram from './RackDiagram';
import RackSummary from './RackSummary';
import UnitEditor from './UnitEditor';
import { useCabinetImport } from './useCabinetImport';
import { printQrLabel, printAllCabinetLabels } from '../print/printing';
import { plural } from '../../domain/plural';
import Icon from '../../components/common/Icon';
import ObjectTitle from '../../components/common/ObjectTitle';
import ui from '../../components/common/ui.module.css';
import styles from './RacksPage.module.css';

export default function RacksPage() {
  const { mag, cabinetId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const grouped = useSelector(selectGrouped);
  const prefixMap = useSelector(selectPrefixMap);
  const cabinets = useSelector((state) => selectCabinets(state, mag));
  const undocumented = useSelector((state) => selectUndocumented(state, mag));

  useEffect(() => {
    if (!mag) return;
    dispatch(loadCabinets(mag));
    dispatch(loadUndocumented(mag));
  }, [mag, dispatch]);

  if (!mag || !grouped[mag]) {
    return (
      <ObjectSidebar
        selectedMag={mag}
        onSelect={(next) => navigate(`/racks/${next}`)}
        homeTitle="Шкафы"
        homeHint="Выберите объект, чтобы увидеть его шкафы"
      >
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>Выберите объект</div>
          <p className={styles.emptyText}>
            У каждого объекта — свои коммуникационные шкафы: что стоит в каком юните, какие
            коммутаторы где размещены.
          </p>
        </div>
      </ObjectSidebar>
    );
  }

  return (
    <ObjectSidebar selectedMag={mag} onSelect={(next) => navigate(`/racks/${next}`)}>
      <div className={styles.panel}>
        {cabinetId ? (
          <CabinetView
            mag={mag}
            cabinetId={Number(cabinetId)}
            cabinets={cabinets}
            prefixMap={prefixMap}
            onBack={() => navigate(`/racks/${mag}`)}
            onOpenSwitch={(sw) => navigate(`/objects/${mag}/${sw}`)}
          />
        ) : (
          <CabinetList
            mag={mag}
            cabinets={cabinets}
            prefixMap={prefixMap}
            undocumented={undocumented}
            onOpen={(id) => navigate(`/racks/${mag}/${id}`)}
          />
        )}
      </div>
    </ObjectSidebar>
  );
}

/* ── Список шкафов объекта ──────────────────────────────────────────── */

function CabinetList({ mag, cabinets, prefixMap, undocumented, onOpen }) {
  const canEdit = useSelector((state) => selectCanEditObject(state, mag));
  const { state: importState, importFile } = useCabinetImport(mag, prefixMap);
  const fileRef = useRef(null);

  const busy = importState.status === 'parsing' || importState.status === 'uploading';

  return (
    <>
      <header className={styles.header}>
        <div className={styles.heading}>
          <ObjectTitle className={styles.title}>Шкафы {objectLabel(mag, prefixMap)}</ObjectTitle>
          <p className={styles.summary}>
            {cabinets.length
              ? `${cabinets.length} ${plural(cabinets.length, 'шкаф', 'шкафа', 'шкафов')}`
              : 'Шкафы не заведены'}
            {undocumented?.total ? `, коммутаторов на объекте ${undocumented.total}` : ''}
          </p>
        </div>
        <div className={styles.actions}>
          {canEdit && (
            <>
              <button
                className={ui.button}
                onClick={() => fileRef.current?.click()}
                disabled={busy}
              >
                <Icon name="upload" size={16} />
                {busy ? 'Загружаю…' : 'Загрузить из файла'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) importFile(file);
                  e.target.value = '';
                }}
              />
            </>
          )}
          {cabinets.length > 0 && (
            <button
              className={ui.button}
              onClick={() =>
                printAllCabinetLabels(
                  objectLabel(mag, prefixMap),
                  cabinets,
                  (cabinet) => `${window.location.origin}/racks/${mag}/${cabinet.id}`
                )
              }
            >
              Наклейки на все шкафы
            </button>
          )}
          <a className={ui.button} href="/Rack-info.xlsx" download>
            Скачать шаблон
          </a>
        </div>
      </header>

      <div className={styles.body}>
        {importState.message && (
          <div className={importState.status === 'error' ? styles.error : styles.success}>
            {importState.message}
          </div>
        )}

        {/* Коммутаторы, не занесённые ни в один шкаф. Это не ошибка
            системы, а признак незаполненных данных — но заметить её
            иначе почти невозможно. */}
        {undocumented?.undocumented?.length > 0 && (
          <div className={styles.warning}>
            <div className={styles.warningTitle}>
              Не размещены ни в одном шкафу: {undocumented.undocumented.length} из{' '}
              {undocumented.total}
            </div>
            <div className={styles.swList}>
              {undocumented.undocumented.map((sw) => (
                <span key={sw} className={styles.swTag}>
                  {sw}
                </span>
              ))}
            </div>
          </div>
        )}

        {cabinets.length === 0 ? (
          <div className={styles.emptyBox}>
            <div className={styles.emptyTitle}>Шкафов пока нет</div>
            <p className={styles.emptyText}>
              Скачайте шаблон, заполните его и загрузите — шкафы и их содержимое появятся здесь.
            </p>
          </div>
        ) : (
          <div className={styles.cards}>
            {cabinets.map((cabinet) => (
              <button className={styles.card} key={cabinet.id} onClick={() => onOpen(cabinet.id)}>
                <span className={styles.cardIcon} aria-hidden="true">
                  <Icon name="racks" size={22} />
                </span>
                <span className={styles.cardText}>
                  <span className={styles.cardName}>{cabinet.name}</span>
                  <span className={styles.cardMeta}>
                    {[cabinet.characteristic, cabinet.location].filter(Boolean).join(', ') ||
                      'Высота и место не указаны'}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ── Содержимое одного шкафа ────────────────────────────────────────── */

function CabinetView({ mag, cabinetId, cabinets, prefixMap, onBack, onOpenSwitch }) {
  const dispatch = useDispatch();
  const items = useSelector((state) => selectCabinetItems(state, cabinetId));
  const canEdit = useSelector((state) => selectCanEditObject(state, mag));

  const [mode, setMode] = useState('visual');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const cabinet = cabinets.find((c) => c.id === cabinetId);

  useEffect(() => {
    dispatch(loadCabinetItems(cabinetId));
  }, [cabinetId, dispatch]);

  const height = useMemo(() => getRackHeight(cabinet?.characteristic, items), [cabinet, items]);

  if (!cabinet) {
    return <div className={styles.empty}>Загрузка…</div>;
  }

  /* Сохранение заменяет содержимое шкафа целиком — так же, как
     заполняется монтажный чертёж. Поэтому список собирается заново
     при каждом изменении и отправляется одним запросом. */
  async function persist(nextItems) {
    setSaving(true);
    setError(null);
    try {
      await dispatch(saveCabinetItems({ cabinetId, items: nextItems })).unwrap();
      setDraft(null);
      /* Список неразмещённых мог измениться — перечитываем. */
      dispatch(loadUndocumented(mag));
    } catch (message) {
      setError(`Не удалось сохранить: ${message}`);
    } finally {
      setSaving(false);
    }
  }

  function handleSelectUnit(unit) {
    if (!canEdit) return;
    const existing = items.find((item) => Number(item.unit) === unit);
    setDraft({
      unit,
      span: 1,
      name: existing?.name ?? '',
      comment: existing?.comment ?? '',
    });
  }

  function handleSaveDraft(values) {
    /* Короткое обозначение из шаблона приводится к полному виду:
       «SW_74» в объекте 253 становится «DS253_74». */
    const name = normalizeSwitchName(values.name, mag, prefixMap);

    /* Юниты, которые займёт оборудование: от указанного вниз. */
    const occupied = [];
    for (let u = values.unit; u > values.unit - values.span; u--) occupied.push(u);

    const next = items
      .filter((item) => !occupied.includes(Number(item.unit)))
      .map((item) => ({ unit: item.unit, name: item.name, comment: item.comment ?? '' }));

    for (const unit of occupied) {
      next.push({ unit, name, comment: values.comment });
    }

    persist(next);
  }

  function handleDeleteDraft({ unit, span }) {
    const freed = [];
    for (let u = unit; u > unit - span; u--) freed.push(u);

    const next = items
      .filter((item) => !freed.includes(Number(item.unit)))
      .map((item) => ({ unit: item.unit, name: item.name, comment: item.comment ?? '' }));

    persist(next);
  }

  function handleAdd() {
    const unit = firstFreeUnit(height, items);
    if (unit === null) {
      setError('В шкафу нет свободных юнитов.');
      return;
    }
    setDraft({ unit, span: 1, name: '', comment: '' });
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.heading}>
          <button className={styles.back} onClick={onBack}>
            ← Шкафы {objectLabel(mag, prefixMap)}
          </button>
          <h1 className={styles.title}>{cabinet.name}</h1>
          <p className={styles.summary}>
            {[cabinet.characteristic, cabinet.location].filter(Boolean).join(', ') ||
              'Высота и место не указаны'}
          </p>
        </div>
        <div className={styles.actions}>
          <div className={styles.segment} role="group" aria-label="Вид">
            <button
              className={`${styles.segButton} ${mode === 'visual' ? styles.segActive : ''}`}
              onClick={() => setMode('visual')}
              aria-pressed={mode === 'visual'}
            >
              Схема
            </button>
            <button
              className={`${styles.segButton} ${mode === 'table' ? styles.segActive : ''}`}
              onClick={() => setMode('table')}
              aria-pressed={mode === 'table'}
            >
              Таблица
            </button>
          </div>
          <button
            className={ui.button}
            onClick={() =>
              printQrLabel({
                title: cabinet.name,
                subtitle: objectLabel(mag, prefixMap),
                url: `${window.location.origin}/racks/${mag}/${cabinetId}`,
                hint: 'Switch Inspector — отсканируйте, чтобы открыть содержимое шкафа',
              })
            }
          >
            Наклейка
          </button>
          {canEdit && (
            <button className={`${ui.button} ${ui.primary}`} onClick={handleAdd} disabled={saving}>
              Добавить оборудование
            </button>
          )}
        </div>
      </header>

      {error && <div className={styles.errorBar}>{error}</div>}

      <div className={styles.body}>
        <div className={styles.layout}>
          <div className={styles.layoutLeft}>
            <RackSummary height={height} items={items} />
          </div>
          <div className={styles.layoutRight}>
            {draft && (
              <UnitEditor
                draft={draft}
                maxUnit={height}
                mag={mag}
                onSave={handleSaveDraft}
                onDelete={handleDeleteDraft}
                onCancel={() => setDraft(null)}
              />
            )}

            {mode === 'visual' ? (
              <RackDiagram
                height={height}
                items={items}
                onSelectUnit={handleSelectUnit}
                onOpenSwitch={onOpenSwitch}
              />
            ) : (
              <ItemTable items={items} onOpenSwitch={onOpenSwitch} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/** Табличный режим: то же содержимое построчно — удобнее для правки
 *  и просмотра комментариев, которые на схеме не помещаются. */
function ItemTable({ items, onOpenSwitch }) {
  const sorted = [...items].sort((a, b) => Number(b.unit) - Number(a.unit));

  if (!sorted.length) {
    return (
      <div className={styles.emptyBox}>
        <div className={styles.emptyTitle}>Шкаф пуст</div>
      </div>
    );
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Юнит</th>
            <th>Оборудование</th>
            <th>Комментарий</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {sorted.map((item) => (
            <tr key={item.unit}>
              <td className={styles.unit}>{item.unit}</td>
              <td className={styles.itemName}>
                {item.name}
                {item.shkafMismatch && (
                  <div className={styles.mismatch}>
                    По учёту числится в шкафу {item.actualShkaf}
                  </div>
                )}
              </td>
              <td className={`${styles.muted} ${styles.itemComment}`}>{item.comment}</td>
              <td className={`${styles.right} ${styles.itemAction}`}>
                {item.switchExists && (
                  <button className={ui.button} onClick={() => onOpenSwitch(item.matchedSw)}>
                    Карточка
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
